import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  ErrorState,
  LoadingState,
  Stat,
} from '@/components/ui/primitives';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/overlays';
import { formatDateTime } from '@/lib/utils';

export function ImportDetailPage() {
  const { id } = useParams();
  const importId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [undoOpen, setUndoOpen] = React.useState(false);

  const detail = useQuery({ queryKey: ['import', importId], queryFn: () => api.getImport(importId) });

  if (detail.isLoading) return <Page title="Import"><LoadingState /></Page>;
  if (detail.error) {
    return (
      <Page title="Import">
        <ErrorState error={detail.error} onRetry={() => detail.refetch()} />
      </Page>
    );
  }

  const imp = detail.data!.import;
  const rows = detail.data!.rows;
  const canUndo = imp.status === 'completed' && !imp.undoneAt;

  return (
    <Page
      title={imp.fileName}
      description={`Import #${imp.id} · ${imp.status} · ${formatDateTime(imp.createdAt)}`}
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={() => navigate('/imports')}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Imports
          </Button>
          {canUndo && (
            <Button variant="outline" size="sm" onClick={() => setUndoOpen(true)}>
              <Undo2 className="h-3.5 w-3.5" />
              Undo this import
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-5 p-5 md:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <Stat label="Rows" value={imp.totalRows.toLocaleString()} />
          <Stat label="Inserted" value={imp.insertedCount.toLocaleString()} valueClassName="text-emerald-700 dark:text-emerald-300" />
          <Stat label="Updated" value={imp.updatedCount.toLocaleString()} />
          <Stat
            label="To review"
            value={imp.duplicateCandidateCount.toLocaleString()}
            valueClassName={imp.duplicateCandidateCount > 0 ? 'text-amber-300' : undefined}
          />
          <Stat label="Skipped" value={imp.skippedCount.toLocaleString()} />
          <Stat
            label="Rejected"
            value={imp.rejectedCount.toLocaleString()}
            valueClassName={imp.rejectedCount > 0 ? 'text-red-300' : undefined}
          />
        </div>

        {imp.errorMessage && (
          <Card className="border-destructive/40">
            <CardBody className="text-xs text-red-300">{imp.errorMessage}</CardBody>
          </Card>
        )}

        {imp.undoneAt && (
          <Card className="border-amber-500/40">
            <CardBody className="text-xs text-amber-300">
              This import was undone on {formatDateTime(imp.undoneAt)}.
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Rows</CardTitle>
            <span className="text-2xs text-muted-foreground">Showing up to 500</span>
          </CardHeader>
          <div className="max-h-[32rem] overflow-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-16 text-right">Row</th>
                  <th className="w-28">Action</th>
                  <th className="min-w-[14rem]">Business</th>
                  <th>Message</th>
                  <th className="w-64">Warnings</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="text-right tabular-nums text-muted-foreground">{row.rowNumber}</td>
                    <td>
                      <Badge
                        variant={
                          row.action === 'inserted'
                            ? 'success'
                            : row.action === 'rejected'
                              ? 'danger'
                              : row.action === 'duplicate_candidate'
                                ? 'warning'
                                : 'default'
                        }
                      >
                        {row.action.replace('_', ' ')}
                      </Badge>
                      {row.rolledBackAt && <Badge className="ml-1">rolled back</Badge>}
                    </td>
                    <td>
                      {row.leadId ? (
                        <Link to={`/leads/${row.leadId}`} className="hover:text-primary hover:underline">
                          {row.businessName ?? `Lead #${row.leadId}`}
                        </Link>
                      ) : row.duplicateCandidateId ? (
                        <Link to="/duplicates" className="text-amber-300 hover:underline">
                          Awaiting duplicate review
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="max-w-md truncate text-muted-foreground">{row.message ?? '—'}</td>
                    <td className="text-2xs text-amber-300">
                      {row.warnings.length > 0 ? row.warnings.join('; ') : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <UndoDialog
        open={undoOpen}
        onOpenChange={setUndoOpen}
        importId={importId}
        onDone={() => {
          queryClient.invalidateQueries({ queryKey: ['import', importId] });
          queryClient.invalidateQueries({ queryKey: ['imports'] });
          queryClient.invalidateQueries({ queryKey: ['leads'] });
        }}
      />
    </Page>
  );
}

function UndoDialog({
  open,
  onOpenChange,
  importId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  importId: number;
  onDone: () => void;
}) {
  const plan = useQuery({
    queryKey: ['undo-plan', importId],
    queryFn: () => api.undoPlan(importId),
    enabled: open,
  });
  const [selected, setSelected] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    if (plan.data) setSelected(new Set(plan.data.safeRows.map((r) => r.rowId)));
  }, [plan.data]);

  const undo = useMutation({
    mutationFn: () => api.undoImport(importId, [...selected]),
    onSuccess: (result) => {
      onDone();
      onOpenChange(false);
      toast.success(
        `${result.deletedLeads} leads removed, ${result.restoredLeads} restored${
          result.blocked ? `, ${result.blocked} left for manual handling` : ''
        }`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Undo import #{importId}</DialogTitle>
          <DialogDescription>
            Rows are only rolled back automatically when nothing has happened to the lead since the import.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {plan.isLoading && <LoadingState />}
          {plan.error && <ErrorState error={plan.error} />}

          {plan.data && (
            <>
              <section>
                <h3 className="section-label mb-1">
                  Safe to roll back ({plan.data.safeRows.length})
                </h3>
                {plan.data.safeRows.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nothing can be rolled back automatically.</p>
                ) : (
                  <div className="max-h-52 overflow-auto rounded border border-border">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="w-8" />
                          <th className="w-16 text-right">Row</th>
                          <th className="w-24">Action</th>
                          <th>Business</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plan.data.safeRows.map((row) => (
                          <tr key={row.rowId}>
                            <td>
                              <Checkbox
                                checked={selected.has(row.rowId)}
                                onCheckedChange={(v) =>
                                  setSelected((prev) => {
                                    const next = new Set(prev);
                                    if (v) next.add(row.rowId);
                                    else next.delete(row.rowId);
                                    return next;
                                  })
                                }
                              />
                            </td>
                            <td className="text-right tabular-nums text-muted-foreground">{row.rowNumber}</td>
                            <td>
                              <Badge variant="outline">{row.action}</Badge>
                            </td>
                            <td className="truncate">{row.businessName ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {plan.data.blockedRows.length > 0 && (
                <section>
                  <h3 className="section-label mb-1 text-amber-300">
                    Blocked — needs manual handling ({plan.data.blockedRows.length})
                  </h3>
                  <div className="max-h-52 overflow-auto rounded border border-amber-500/40">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="w-16 text-right">Row</th>
                          <th className="min-w-[12rem]">Business</th>
                          <th>Why it is blocked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plan.data.blockedRows.map((row) => (
                          <tr key={row.rowId}>
                            <td className="text-right tabular-nums text-muted-foreground">{row.rowNumber}</td>
                            <td>
                              {row.leadId ? (
                                <Link to={`/leads/${row.leadId}`} className="hover:text-primary hover:underline">
                                  {row.businessName ?? `Lead #${row.leadId}`}
                                </Link>
                              ) : (
                                (row.businessName ?? '—')
                              )}
                            </td>
                            <td className="text-amber-300">{row.blockingReasons.join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={selected.size === 0}
            loading={undo.isPending}
            onClick={() => undo.mutate()}
          >
            Roll back {selected.size} row{selected.size === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
